import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  PaperPlaneRight,
  FilePdf,
  FileXls,
  CheckCircle,
  Clock,
  ArrowRight,
  WarningCircle,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusMap = {
  not_sent: { label: 'Da comunicare', color: 'bg-amber-100 text-amber-800', icon: Clock },
  draft: { label: 'Bozza', color: 'bg-blue-100 text-blue-800', icon: Clock },
  documents_ready: { label: 'Documenti pronti', color: 'bg-indigo-100 text-indigo-800', icon: FilePdf },
  sent: { label: 'Inviata', color: 'bg-green-100 text-green-800', icon: CheckCircle },
};

const CommunicationsPage = () => {
  const { axiosInstance } = useAuth();
  const [provinces, setProvinces] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [provRes, commRes] = await Promise.all([
        axiosInstance.get('/communications/provinces-available'),
        axiosInstance.get('/communications'),
      ]);
      setProvinces(provRes.data);
      setCommunications(commRes.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const setLoaderFor = (key, val) => setActionLoading((p) => ({ ...p, [key]: val }));

  const handleGenerateDraft = async (provinceCode) => {
    setLoaderFor(provinceCode + '_draft', true);
    try {
      await axiosInstance.post(`/communications/generate/${provinceCode}`);
      toast.success(`Bozza creata per provincia ${provinceCode}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione bozza');
    } finally {
      setLoaderFor(provinceCode + '_draft', false);
    }
  };

  const handleGenerateDocuments = async (provinceCode) => {
    setLoaderFor(provinceCode + '_docs', true);
    try {
      await axiosInstance.post(`/communications/${provinceCode}/generate-documents`);
      toast.success(`PDF e Excel generati per provincia ${provinceCode}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore generazione documenti');
    } finally {
      setLoaderFor(provinceCode + '_docs', false);
    }
  };

  const handleDownloadPdf = (provinceCode) => {
    window.open(`${API_URL}/api/communications/${provinceCode}/download-pdf`, '_blank');
  };

  const handleDownloadExcel = (provinceCode) => {
    window.open(`${API_URL}/api/communications/${provinceCode}/download-excel`, '_blank');
  };

  const handleSendPec = async (provinceCode) => {
    setLoaderFor(provinceCode + '_pec', true);
    try {
      const { data } = await axiosInstance.post(`/communications/${provinceCode}/send-pec`);
      toast.success(data.message || 'PEC inviata con successo');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio PEC');
    } finally {
      setLoaderFor(provinceCode + '_pec', false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-[#0A2540] border-r-transparent"></div>
          <p className="mt-4 text-sm text-slate-600">Caricamento comunicazioni...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="communications-page">
      <div>
        <h1 className="text-4xl font-extrabold tracking-tighter text-slate-900">Comunicazioni Province</h1>
        <p className="mt-2 text-sm text-slate-600">
          Gestione comunicazioni di apertura attivita alle UADM competenti
        </p>
      </div>

      {provinces.length === 0 ? (
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-6 text-center">
          <WarningCircle size={32} className="mx-auto text-amber-600 mb-3" />
          <p className="text-sm text-amber-900 font-medium">Nessuna provincia trovata</p>
          <p className="text-xs text-amber-700 mt-1">Importa prima l'anagrafica POD per visualizzare le province attive</p>
        </div>
      ) : (
        <div className="space-y-4">
          {provinces.map((prov) => {
            const status = statusMap[prov.communication_status] || statusMap.not_sent;
            const StatusIcon = status.icon;
            const code = prov.province;

            return (
              <div
                key={code}
                className="bg-white border border-slate-200 rounded-sm p-6 hover:border-slate-300 transition-colors"
                data-testid={`province-card-${code}`}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-slate-900">Provincia: {code}</h3>
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-medium ${status.color}`}>
                        <StatusIcon size={14} weight="fill" />
                        {status.label}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-xs text-slate-500">POD attivi</p>
                        <p className="font-semibold text-slate-900">{prov.pod_count}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Accise annue stimate</p>
                        <p className="font-semibold text-slate-900">EUR {(prov.total_excise || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">UADM competente</p>
                        <p className="font-semibold text-slate-900 text-xs">{prov.customs_office}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">PEC UADM</p>
                        <p className="font-semibold text-slate-900 text-xs break-all">{prov.customs_pec}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {prov.communication_status === 'not_sent' && (
                      <Button
                        size="sm"
                        onClick={() => handleGenerateDraft(code)}
                        disabled={actionLoading[code + '_draft']}
                        className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-sm text-xs"
                        data-testid={`generate-draft-${code}`}
                      >
                        {actionLoading[code + '_draft'] ? 'Creazione...' : 'Crea Bozza'}
                      </Button>
                    )}

                    {prov.communication_status === 'draft' && (
                      <Button
                        size="sm"
                        onClick={() => handleGenerateDocuments(code)}
                        disabled={actionLoading[code + '_docs']}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-sm text-xs"
                        data-testid={`generate-docs-${code}`}
                      >
                        <FilePdf size={14} className="mr-1" />
                        {actionLoading[code + '_docs'] ? 'Generazione...' : 'Genera PDF + Excel'}
                      </Button>
                    )}

                    {(prov.communication_status === 'documents_ready' || prov.communication_status === 'sent') && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownloadPdf(code)}
                          className="rounded-sm text-xs"
                          data-testid={`download-pdf-${code}`}
                        >
                          <FilePdf size={14} className="mr-1 text-red-600" />
                          PDF
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownloadExcel(code)}
                          className="rounded-sm text-xs"
                          data-testid={`download-excel-${code}`}
                        >
                          <FileXls size={14} className="mr-1 text-green-600" />
                          Excel
                        </Button>
                      </>
                    )}

                    {prov.communication_status === 'documents_ready' && (
                      <Button
                        size="sm"
                        onClick={() => handleSendPec(code)}
                        disabled={actionLoading[code + '_pec']}
                        className="bg-green-600 hover:bg-green-700 text-white rounded-sm text-xs"
                        data-testid={`send-pec-${code}`}
                      >
                        <PaperPlaneRight size={14} className="mr-1" />
                        {actionLoading[code + '_pec'] ? 'Invio...' : 'Invia PEC'}
                      </Button>
                    )}

                    {prov.communication_status === 'sent' && (
                      <span className="inline-flex items-center gap-1 text-xs text-green-700 font-medium px-3 py-1.5">
                        <CheckCircle size={14} weight="fill" />
                        Inviata il {prov.communication_date ? new Date(prov.communication_date).toLocaleDateString('it-IT') : '-'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CommunicationsPage;
